/**
 * useComicEpisodeContent - Fetch comic episode content for reader
 * Location: /app/frontend/src/components/page-builder/reader/hooks/
 */

import { useState, useEffect } from 'react';
import { apiClient } from '../../../../config/api';

/**
 * Hook to fetch comic episode content (pages/panels)
 * @param {string} seriesId - The series ID
 * @param {string} episodeId - The episode ID
 * @param {object} options - Optional config { version: 'draft' | 'published' }
 */
export const useComicEpisodeContent = (seriesId, episodeId, options = {}) => {
  const [content, setContent] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!seriesId || !episodeId) {
      setLoading(false);
      setError('Missing series or episode ID');
      return;
    }

    const fetchContent = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try the read endpoint first
        const versionParam = options.version === 'draft' ? '?version=draft' : '';
        const response = await apiClient.get(
          `/series/${seriesId}/episodes/${episodeId}/read${versionParam}`
        );

        const data = response.data;
        
        // Normalize content structure
        setContent({
          pages: data.pages || data.panels || data.images || [],
          type: 'comic',
          totalPages: (data.pages || data.panels || data.images || []).length,
        });

        setMetadata({
          id: data.id || episodeId,
          title: data.title || 'Episode',
          seriesId: seriesId,
          seriesTitle: data.series_title || data.seriesTitle || '',
          episodeNumber: data.episode_number || data.order || 1,
          nextEpisodeId: data.next_episode_id || null,
          prevEpisodeId: data.prev_episode_id || null,
          createdAt: data.created_at,
          publishedAt: data.published_at,
        });

      } catch (err) {
        console.error('[useComicEpisodeContent] Error:', err);
        
        // Fallback: try to get episode data directly
        try {
          const fallbackResponse = await apiClient.get(`/chapters/${episodeId}`);
          const fallbackData = fallbackResponse.data;
          
          setContent({
            pages: fallbackData.pages || fallbackData.panels || [],
            type: 'comic',
            totalPages: (fallbackData.pages || fallbackData.panels || []).length,
          });

          setMetadata({
            id: fallbackData.id || episodeId,
            title: fallbackData.title || 'Episode',
            seriesId: seriesId,
            seriesTitle: fallbackData.series_title || '',
            episodeNumber: fallbackData.order || 1,
          });

        } catch (fallbackErr) {
          setError(err.response?.data?.detail || err.message || 'Failed to load episode');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [seriesId, episodeId, options.version]);

  return { content, metadata, loading, error, refetch: () => setLoading(true) };
};

export default useComicEpisodeContent;
